import React, { memo, PropsWithChildren } from 'react';
import { StyleSheet, View } from 'react-native';

import { PortalProvider } from '@gorhom/portal';

import { theme } from '@sg/garnish';

import { useAppsFlyerInitialization } from '@order/integrations-appsflyer';
import { useAwsWafScript } from '@order/integrations-aws-waf';
import { UsableNetBanner } from '@order/usable-net';

import {
  GroupOrderTerminalStateModal,
  OtaUpdateDialog,
  useResetDietaryRestrictionsOnLogout,
  useResetLoyaltyOffersOnLogout,
} from './components';
import { useValidOrders } from './context';
import { useCustomerTelemetry, useCustomer } from './context/Customer';
import { useInitializeDietaryPreferences } from './context/DietaryPreferences';
import { useTrackLastInteractedStore } from './context/LastInteractedStore';
import { useResetPlacedOrdersCounterOnLogout } from './context/PlacedOrdersCounter';
import { useUrqlContext } from './context/Urql';
import {
  useAccessibilityTracking,
  useAppTrackingTransparency,
  useCancelLiveActivitiesOnLogout,
  useFetchUserCart,
  useGroupOrderingState,
  useHasUnseenChallenges,
  useLocationPermissionTracking,
  useLoyaltyPremiumStatus,
  useNetworkSubscription,
  useOrderStatusNotificationPreferences,
  usePushNotifications,
  useRegisterUrqlErrorTracker,
  useRegisterUrqlLogOutHandler,
  useResetGroupOrderReferenceOnLogout,
  useResetSierraOnLogout,
  useTrackSmartAppBanner,
} from './hooks';
import { useLiveActivityTelemetry } from './integrations/live-activities';

// ────────────────────────────────────────────────────────────────────────────────

export const AppContent = memo((props: PropsWithChildren) => {
  const { children } = props;

  const { isResettingClient } = useUrqlContext();

  useCustomerTelemetry();
  useNetworkSubscription();
  usePushNotifications();
  useTrackLastInteractedStore();
  useAccessibilityTracking();
  useLocationPermissionTracking();
  useRegisterUrqlErrorTracker();
  useRegisterUrqlLogOutHandler();
  useLiveActivityTelemetry();
  useTrackSmartAppBanner();
  useValidOrders('network-only');
  useAwsWafScript();
  useInitializeDietaryPreferences();

  // ─── Transparency & Marketing ──────────────────────────────────────────────

  const { customer } = useCustomer();
  const isTrackingAllowed = useAppTrackingTransparency();
  useAppsFlyerInitialization(isTrackingAllowed, customer?.trackingUuid);

  // ─── App Load Graphql Updates ────────────────────────────────────────

  useLoyaltyPremiumStatus({ requestPolicy: 'cache-and-network' });
  useOrderStatusNotificationPreferences({ requestPolicy: 'cache-and-network' });

  // ─── Logout Resets ───────────────────────────────────────────────────

  useResetDietaryRestrictionsOnLogout();
  useResetGroupOrderReferenceOnLogout();
  useResetPlacedOrdersCounterOnLogout();
  useResetSierraOnLogout();
  useResetLoyaltyOffersOnLogout();
  useCancelLiveActivitiesOnLogout();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View testID="app.container" style={styles.appContainer}>
      <UsableNetBanner />

      <PortalProvider>
        {isResettingClient ? null : <AuthGraphqlEffects />}

        {isResettingClient ? null : <GroupOrderEffects />}
        <OtaUpdateDialog />

        {children}
      </PortalProvider>
    </View>
  );
});

// ─── Components ──────────────────────────────────────────────────────────────

/**
 * Hosts auth-scoped GraphQL effects that must unmount while the URQL client is
 * resetting on logout.
 */
const AuthGraphqlEffects = () => {
  useFetchUserCart();
  useHasUnseenChallenges('cache-and-network');

  return null;
};

/**
 * Hosts group-order synchronization and app-level terminal-state UI.
 */
const GroupOrderEffects = () => {
  const { terminalGroupOrder, dismissTerminalGroupOrder } = useGroupOrderingState();

  return terminalGroupOrder ? (
    <GroupOrderTerminalStateModal
      hasCurrentUserItems={terminalGroupOrder.hasCurrentUserItems}
      hostDisplayName={terminalGroupOrder.hostDisplayName}
      orderChannel={terminalGroupOrder.orderChannel}
      orderState={terminalGroupOrder.orderState}
      onDismiss={dismissTerminalGroupOrder}
    />
  ) : null;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  appContainer: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
});
